import { CheckCircleIcon } from "@phosphor-icons/react";
import { ONBOARDING_LEAGUES, CONTENT_STYLES, LANGUAGES } from "@/lib/constants";

/* Each step is a small, pure presentational component.
   Keeps the parent Onboarding wizard's cyclomatic complexity low. */

export function StepCard({ title, subtitle, children, testId }) {
  return (
    <section className="p-5 md:p-7 rounded-md bg-[#121212] border border-white/10 fade-up" data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A84FF] mb-1">My Fan DNA</p>
      <h1 className="font-display font-bold text-3xl md:text-4xl uppercase tracking-tighter">{title}</h1>
      {subtitle && <p className="text-sm text-white/60 mt-1 mb-6">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function StepLeagues({ value, onToggle }) {
  return (
    <StepCard title="Pick your leagues" subtitle="Which sports do you actually watch?" testId="step-leagues">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {ONBOARDING_LEAGUES.map((lg) => {
          const active = value.includes(lg);
          return (
            <button
              type="button"
              key={lg}
              onClick={() => onToggle(lg)}
              data-testid={`onb-league-${lg}`}
              className={`p-5 rounded-md text-center border transition-colors ${
                active ? "border-[#0A84FF] bg-[#0A84FF]/10" : "border-white/10 bg-[#121212] hover:border-white/25"
              }`}
            >
              <p className="font-display font-bold text-2xl uppercase tracking-tight">{lg}</p>
              {active && <CheckCircleIcon weight="fill" className="text-[#0A84FF] mx-auto mt-2" />}
            </button>
          );
        })}
      </div>
    </StepCard>
  );
}

export function StepTeams({ teams, value, onToggle }) {
  return (
    <StepCard title="Pick your teams" subtitle="Tap as many as you want. Your dashboard learns from these." testId="step-teams">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {teams.map((t) => {
          const key = `${t.league}:${t.abbr}`;
          const active = value.includes(key);
          return (
            <button
              type="button"
              key={key}
              data-testid={`onb-team-${t.league}-${t.abbr}`}
              onClick={() => onToggle(key)}
              className={`p-3 rounded-md text-left transition-colors border ${
                active ? "border-[#32D74B] bg-[#32D74B]/5" : "border-white/10 bg-[#121212] hover:border-white/25"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className="w-9 h-9 rounded flex items-center justify-center font-display font-bold text-sm"
                  style={{ backgroundColor: `${t.color}22`, color: t.color, border: `1px solid ${t.color}55` }}
                >
                  {t.abbr}
                </div>
                {active && <CheckCircleIcon weight="fill" className="text-[#32D74B]" />}
              </div>
              <p className="font-display font-bold text-sm uppercase tracking-tight truncate">{t.name}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">{t.league}</p>
            </button>
          );
        })}
        {teams.length === 0 && <p className="col-span-full text-white/50 text-sm">Select a league first.</p>}
      </div>
    </StepCard>
  );
}

export function StepPlayers({ players, value, onToggle }) {
  return (
    <StepCard title="Pick your players" subtitle="Storyline + fantasy alerts will track these stars." testId="step-players">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {players.map((p) => {
          const active = value.includes(p.id);
          return (
            <button
              type="button"
              key={p.id}
              data-testid={`onb-player-${p.id}`}
              onClick={() => onToggle(p.id)}
              className={`p-4 rounded-md text-left transition-colors border ${
                active ? "border-[#32D74B] bg-[#32D74B]/5" : "border-white/10 bg-[#121212] hover:border-white/25"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{p.league} · {p.team}</span>
                {active && <CheckCircleIcon weight="fill" className="text-[#32D74B]" />}
              </div>
              <p className="font-display font-bold text-lg uppercase tracking-tight">{p.name}</p>
              <p className="text-[11px] text-white/55 mt-0.5 line-clamp-2">{p.storyline}</p>
            </button>
          );
        })}
        {players.length === 0 && <p className="col-span-full text-white/50 text-sm">No players for those leagues yet.</p>}
      </div>
    </StepCard>
  );
}

export function StepStyle({ value, onChange }) {
  return (
    <StepCard title="What's your content style?" subtitle="We'll tune the AI's voice to match." testId="step-style">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {CONTENT_STYLES.map((s) => {
          const active = value === s.value;
          return (
            <button
              type="button"
              key={s.value}
              data-testid={`onb-style-${s.value}`}
              onClick={() => onChange(s.value)}
              className={`p-5 rounded-md text-left transition-colors border ${
                active ? "border-[#0A84FF] bg-[#0A84FF]/10" : "border-white/10 bg-[#121212] hover:border-white/25"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="font-display font-bold text-lg uppercase tracking-tight">{s.label}</p>
                {active && <CheckCircleIcon weight="fill" className="text-[#0A84FF]" />}
              </div>
              <p className="text-sm text-white/60">{s.desc}</p>
            </button>
          );
        })}
      </div>
    </StepCard>
  );
}

export function StepLanguage({ value, onChange }) {
  return (
    <StepCard
      title="Pick your language"
      subtitle="Affects 'Why It Matters', storylines, chat, and Creator Studio output."
      testId="step-language"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {LANGUAGES.map((l) => {
          const active = value === l.value;
          return (
            <button
              type="button"
              key={l.value}
              data-testid={`onb-lang-${l.value}`}
              onClick={() => onChange(l.value)}
              className={`p-6 rounded-md text-center transition-colors border ${
                active ? "border-[#32D74B] bg-[#32D74B]/5" : "border-white/10 bg-[#121212] hover:border-white/25"
              }`}
            >
              <p className="font-display font-bold text-2xl uppercase tracking-tighter">{l.label}</p>
              {active && <CheckCircleIcon weight="fill" className="text-[#32D74B] mx-auto mt-2" />}
            </button>
          );
        })}
      </div>
    </StepCard>
  );
}
